import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Coin } from "../components/ui/Coin.js";
import { Icon } from "../components/ui/Icon.js";
import {
  createDcaPlan,
  deleteDcaPlan,
  fetchDcaPlans,
  type DcaPlan,
} from "../lib/api.js";

const FREQS = ["DAILY", "WEEKLY", "BIWEEKLY", "MONTHLY"] as const;
const EXCHANGES = ["binance", "bybit", "okx", "kraken", "coinbase"] as const;

function coinSym(s: string): string {
  return s.replace(/USDT$|USD$|USDC$/, "");
}

function fmtDate(ms: number): string {
  return new Date(ms).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function DcaPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["dca"], queryFn: fetchDcaPlans });
  const [open, setOpen] = useState(false);

  const remove = useMutation({
    mutationFn: (id: string) => deleteDcaPlan(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["dca"] }),
  });

  return (
    <div style={{ padding: 18 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: 14,
        }}
      >
        <div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>DCA Automation</div>
          <div className="muted" style={{ fontSize: 11 }}>
            Scheduled spot BUY orders · spread cost basis over time
          </div>
        </div>
        <button
          type="button"
          className="se-btn se-btn-primary"
          style={{ marginLeft: "auto" }}
          onClick={() => setOpen(true)}
        >
          <Icon name="plus" size={13} /> New plan
        </button>
      </div>

      <div className="se-card">
        <table className="se-table">
          <thead>
            <tr>
              <th>Asset</th>
              <th>Exchange</th>
              <th>Amount</th>
              <th>Frequency</th>
              <th>Next run</th>
              <th>Invested</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {(q.data ?? []).length === 0 ? (
              <tr>
                <td colSpan={8} style={{ padding: 24, textAlign: "center" }} className="muted">
                  No DCA plans yet. Create one to start dollar-cost averaging.
                </td>
              </tr>
            ) : (
              (q.data ?? []).map((p) => (
                <tr key={p.id}>
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <Coin sym={coinSym(p.symbol)} size="sm" />
                      <span style={{ fontWeight: 500 }}>{p.symbol}</span>
                    </div>
                  </td>
                  <td className="muted">{p.exchange}</td>
                  <td className="num">
                    {p.quoteAmount.toLocaleString()} {p.quoteCurrency}
                  </td>
                  <td>
                    <span className="se-tag">{p.frequency}</span>
                  </td>
                  <td className="num">{fmtDate(p.nextRunAt)}</td>
                  <td className="num">
                    {p.totalInvested.toLocaleString()} {p.quoteCurrency}
                  </td>
                  <td>
                    <span
                      className={`se-pill ${
                        p.status === "ACTIVE"
                          ? "se-pill-buy"
                          : p.status === "PAUSED"
                            ? "se-pill-warn"
                            : "se-pill-muted"
                      }`}
                    >
                      {p.status}
                    </span>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="se-btn se-btn-ghost se-btn-sm"
                      onClick={() => remove.mutate(p.id)}
                      title="Delete"
                    >
                      <Icon name="x" size={12} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {open ? (
        <NewPlanModal
          onClose={() => setOpen(false)}
          onCreated={() => {
            setOpen(false);
            qc.invalidateQueries({ queryKey: ["dca"] });
          }}
        />
      ) : null}
    </div>
  );
}

interface NewPlanModalProps {
  onClose: () => void;
  onCreated: () => void;
}

function NewPlanModal({ onClose, onCreated }: NewPlanModalProps) {
  const [exchange, setExchange] = useState<DcaPlan["exchange"]>("binance");
  const [symbol, setSymbol] = useState("BTCUSDT");
  const [amount, setAmount] = useState(100);
  const [frequency, setFrequency] = useState<DcaPlan["frequency"]>("WEEKLY");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(): Promise<void> {
    setSubmitting(true);
    setError(null);
    try {
      await createDcaPlan({
        exchange,
        symbol,
        quoteAmount: amount,
        quoteCurrency: "USDT",
        frequency,
        nextRunAt: Date.now() + 60_000,
      });
      onCreated();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 100,
      }}
      onClick={onClose}
    >
      <div
        className="se-card"
        style={{ width: 420, padding: 20 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>New DCA plan</div>

        <label className="muted" style={{ fontSize: 11, display: "block" }}>Exchange</label>
        <select
          className="se-input"
          value={exchange}
          onChange={(e) => setExchange(e.target.value)}
          style={{ marginBottom: 10 }}
        >
          {EXCHANGES.map((x) => (
            <option key={x} value={x}>{x}</option>
          ))}
        </select>

        <label className="muted" style={{ fontSize: 11, display: "block" }}>Symbol</label>
        <input
          className="se-input mono"
          value={symbol}
          onChange={(e) => setSymbol(e.target.value.toUpperCase())}
          style={{ marginBottom: 10 }}
        />

        <label className="muted" style={{ fontSize: 11, display: "block" }}>Amount (USDT)</label>
        <input
          className="se-input mono"
          type="number"
          min="1"
          value={amount}
          onChange={(e) => setAmount(Number(e.target.value))}
          style={{ marginBottom: 10 }}
        />

        <label className="muted" style={{ fontSize: 11, display: "block" }}>Frequency</label>
        <select
          className="se-input"
          value={frequency}
          onChange={(e) => setFrequency(e.target.value as DcaPlan["frequency"])}
          style={{ marginBottom: 16 }}
        >
          {FREQS.map((f) => (
            <option key={f} value={f}>{f}</option>
          ))}
        </select>

        {error ? (
          <div style={{ color: "var(--sell)", fontSize: 11, marginBottom: 10 }}>{error}</div>
        ) : null}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button type="button" className="se-btn" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="se-btn se-btn-primary"
            disabled={submitting}
            onClick={submit}
          >
            {submitting ? "Creating…" : "Create plan"}
          </button>
        </div>
      </div>
    </div>
  );
}
